import { IELTData } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import {
  ELT_FORM_SECTION,
  IEltDocFormData,
  TComplexSectionData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { BarChart } from 'legacy/lut/components/widgets/public/partner360/common/reports/barChart/BarChart'
import { QuarterlyViewMarketTable } from 'legacy/lut/components/widgets/public/partner360/common/reports/quarterlyViewMarketTable/QuarterlyViewMarketTable'
import { QuarterlyViewTotal } from 'legacy/lut/components/widgets/public/partner360/common/reports/quarterlyViewTotal/QuarterlyViewTotal'
import { TSobReportSalesType } from 'legacy/lut/components/widgets/public/partner360/configs/reports'
import { getFormedEltTranslation } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getFormedEltTranslation'
import React, { useCallback, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Flex } from 'core-components/library/flex'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { getPreviousQuarterAndYear } from '../../../helpers/getPreviousYearAndQuarter'
import { getQuartersUpToQuarter } from '../../../helpers/getQuartersUpToQuarter'
import { isVersionForGlobalPartner } from '../../../helpers/isVersionForGlobalPartner'

import styles from './BusinessPerformance.module.scss'

interface IBusinessPerformanceProps {
  formData: IEltDocFormData
  netNdpValue: ActualsValueTypes
  data: IELTData
  onChangeComplexFormData: (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => void
  partnerId: string
}

export const BusinessPerformance = ({
  formData,
  data,
  netNdpValue,
  onChangeComplexFormData,
  partnerId,
}: IBusinessPerformanceProps) => {
  const isGlobalPartner = useMemo(() => isVersionForGlobalPartner(partnerId), [partnerId])

  const { year, quarter } = useMemo(() => {
    return getPreviousQuarterAndYear(data.FYear, data.FQuarter)
  }, [data.FYear, data.FQuarter])

  const onRowToggle = useCallback(
    (category: string) => (itemId: string, isExpanded: boolean) => {
      const newBusinessPerfData = formData.BusinessPerformanceData.map((catData) => {
        if (catData.Category !== category) {
          return catData
        }

        const newMarketTableData = catData.MarketTableData.map((row) => {
          if (row.itemId !== itemId) {
            return row
          }
          return {
            ...row,
            isExpanded: isExpanded,
          }
        })

        return {
          ...catData,
          MarketTableData: newMarketTableData,
        }
      })

      onChangeComplexFormData(ELT_FORM_SECTION.BUSINESS_PERF_DATA)(newBusinessPerfData)
    },
    [formData.BusinessPerformanceData, onChangeComplexFormData]
  )

  if (!formData.IsBusinessPerformanceTabEnabled) {
    return (
      <div className={styles.warning}>
        <Translation text={getFormedEltTranslation('business-perf-turn-on-checkbox')} />
      </div>
    )
  }

  if (formData.BusinessPerformanceData?.length === 0) {
    return (
      <div className={styles.warning}>
        <Translation text={getFormedEltTranslation('business-perf-no-data')} />
      </div>
    )
  }

  return (
    <div>
      <div className={styles.title}>
        <Translation
          text={getFormedEltTranslation('business-performance-title')}
          replaceParams={[
            { key: '{netNdp}', value: netNdpValue },
            { key: '{fyear}', value: String(year).slice(-2) },
            { key: '{fquarter}', value: String(quarter) },
          ]}
        />
      </div>
      <div className={styles.root}>
        {formData.BusinessPerformanceData?.map((el) => (
          <div className={styles.category} key={el.Category}>
            <div>
              <Translation text={el.Category} />
            </div>

            <QuarterlyViewTotal
              totalData={el.CollectedData}
              selectedQuarters={getQuartersUpToQuarter(quarter)}
              currentYear={year}
              sellType={netNdpValue as TSobReportSalesType}
              showPartnersCount={isGlobalPartner}
            />

            <QuarterlyViewMarketTable
              marketTableData={el.MarketTableData}
              selectedQuarters={getQuartersUpToQuarter(quarter)}
              currentYear={year}
              selectedCategory={el.Category}
              onRowToggle={onRowToggle(el.Category)}
              sellType={netNdpValue as TSobReportSalesType}
              showPartnersCount={isGlobalPartner}
              marketTableTotalData={el.MarketTableDataTotal}
            />
          </div>
        ))}
        <Flex flexWrap={'wrap'} justifyContent={'space-between'}>
          {formData.BusinessPerformanceData?.map((el) => (
            <div className={styles.chart} key={el.Category}>
              <div className={styles.category}>
                <Translation span text={getFormedEltTranslation('business-perf-chat-category')} />
                &nbsp; – &nbsp;
                <Translation span text={el.Category} />
              </div>

              <BarChart
                currentQuarter={quarter}
                selectedFYear={year}
                className={styles.barChart}
                chartTitleTranslationKey={
                  isGlobalPartner
                    ? getFormedEltTranslation('business-performance-chat-desc')
                    : undefined
                }
                uniqByPartnersLength={el.CollectedData.partnersCount}
                shouldShowFutureYears={false}
                chartData={el.ChartData}
                withoutTitle
                sellType={netNdpValue as TSobReportSalesType}
              />
            </div>
          ))}
        </Flex>
      </div>
    </div>
  )
}
